import { useCallback, useMemo } from 'react';
import { NonWorkingHoursType } from '../contexts/types';
import { useSpreadSheetSync, SpreadSheetSyncConfig } from './useSpreadSheetSync';
import GoogleAPI from '../utils/GoogleAPI';
import i18n from '../utils/i18n';
import { NonWorkingItems, NonWorkingItemsDisplayOrder } from '../contexts/constants';
import {
  getMonthNames,
  getMonthNamesAprilStart,
  shiftToAprilStart,
  shiftToJanuaryStart
} from './useWorkingDaysSpreadSheet';

const T_PREFIX = 'features.InvestmentScreening.NonWorkingHours.spreadsheet';

/**
 * 非稼働時間データの年度ごとの型
 */
export interface NonWorkingHoursYearData {
  fiscalYear: string;
  nonWorkingHoursByMonth: NonWorkingHoursType[];
}

/**
 * 非稼働時間データ変換結果
 */
export interface NonWorkingHoursConversionResult {
  data: NonWorkingHoursYearData[];
  errors: {
    workingHoursErrors: string[];
    nonWorkingHourErrors: string[];
    invalidValueErrors: string[];
  };
}

/** 非稼働項目コード ⇒ スプレッドシート行ラベルキーのマッピング */
const ROW_LABEL_KEY_BY_CODE: Record<string, string> = {
  [NonWorkingItems.MechanicalTrouble.code]: 'machineTrouble',
  [NonWorkingItems.WaitingForParts.code]: 'waitingForParts',
  [NonWorkingItems.Setup.code]: 'setup',
  [NonWorkingItems.Maintenance.code]: 'maintenance',
  [NonWorkingItems.Cleaning.code]: 'cleaning',
  [NonWorkingItems.DimensionAdjustment.code]: 'dimensionAdjustment',
  [NonWorkingItems.WaitingForQualityControl.code]: 'waitingForQualityControl',
  [NonWorkingItems.WaitingForFA.code]: 'waitingForFA',
  [NonWorkingItems.Other.code]: 'other',
  [NonWorkingItems.BreakTime.code]: 'breakTime',
  [NonWorkingItems.PlannedStoppage.code]: 'plannedStoppage',
};

/** 非稼働項目名配列（画面表示順。NonWorkingItemsDisplayOrder と対応） */
const NON_WORKING_ITEM_NAMES = () => NonWorkingItemsDisplayOrder.map(
  (code) => i18n.t(`${T_PREFIX}.rowLabel.${ROW_LABEL_KEY_BY_CODE[code]}`)
);

/** デフォルト時間 */
const DEFAULT_TIME = "00:00";
const DEFAULT_SHIFT_SYSTEM = "1";

/**
 * 時間文字列を分に変換
 */
export const hourMinutesStringToMinutes = (timeStr: string): number => {
  if (!timeStr || timeStr === "") return 0;
  const parts = timeStr.split(":");
  if (parts.length !== 2) return 0;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  return hours * 60 + minutes;
};

/**
 * 分を時間文字列に変換
 */
export const minutesToHourMinutesString = (minutes: number): string => {
  if (minutes < 0) minutes = 0;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * 非稼働時間データをSpreadSheetデータに変換
 */
export const nonWorkingHoursToSpreadSheetData = (
  data: NonWorkingHoursYearData[]
): any[][] => {
  const requiredOverTimesHint = [i18n.t(`${T_PREFIX}.requiredOverTimesHint`), ...Array(12).fill("")];
  const nonWorkingItemsText = [i18n.t(`${T_PREFIX}.rowLabel.nonWorkingItems`), ...Array(12).fill("")];
  const itemNames = NON_WORKING_ITEM_NAMES();

  const sheetData = data.flatMap(yearData => {
    const year = yearData.fiscalYear ?? "";
    const workTime = yearData.nonWorkingHoursByMonth.map(m => m.workTime);
    const overTime = yearData.nonWorkingHoursByMonth.map(m => m.overTime);
    const shiftSystem = yearData.nonWorkingHoursByMonth.map(m => m.shiftSystem);
    // 非稼働項目を画面表示順（NonWorkingItemsDisplayOrder）に並び替えて取得
    const nonWorkingItems = yearData.nonWorkingHoursByMonth.map(m =>
      NonWorkingItemsDisplayOrder.map(code =>
        // 項目が追加される前に保存されたデータには存在しない項目（休憩時間・計画停止など）が
        // あるため、既定値で補完する（補完しないとシート上で空欄表示になる）
        m.nonWorkingItems?.find(d => d.nonWorkingId === code)?.nonWorkingHour ?? DEFAULT_TIME
      )
    );
    const nonWorkingItemsShift = shiftToAprilStart(nonWorkingItems);

    const transpose = (matrix: (string[] | undefined)[]) => {
      if (!matrix[0]) return [];
      return matrix[0].map((_, colIndex) =>
        matrix.map(row => row?.[colIndex] ?? "")
      );
    };
    const nonWorkingItemsArray = transpose(nonWorkingItemsShift);

    // 残業有無（表示専用。残業時間欄に値が入っているかどうかから算出する）
    const requiredOverTimesArray = overTime.map(value => (value && value !== DEFAULT_TIME) ? "〇" : "");

    // 非稼働項目の行（画面表示順で1項目1行）
    const nonWorkingItemRows = itemNames.map((name, itemIndex) => (
      [`　${name}`, ...(nonWorkingItemsArray?.[itemIndex] ?? Array(12).fill(""))]
    ));

    return [
      [year, ...getMonthNamesAprilStart()],
      [i18n.t(`${T_PREFIX}.rowLabel.standardWorkTime`), ...shiftToAprilStart(workTime)],
      [i18n.t(`${T_PREFIX}.rowLabel.overTime`), ...shiftToAprilStart(overTime)],
      [i18n.t(`${T_PREFIX}.rowLabel.shiftSystem`), ...shiftToAprilStart(shiftSystem)],
      [i18n.t(`${T_PREFIX}.rowLabel.overTimePresence`), ...shiftToAprilStart(requiredOverTimesArray)],
      [...nonWorkingItemsText],
      ...nonWorkingItemRows,
      []
    ];
  });

  return [requiredOverTimesHint, ...sheetData];
};

/**
 * SpreadSheetデータを非稼働時間データに変換
 */
export const spreadSheetDataToNonWorkingHours = (
  values: any[][],
  existingData: NonWorkingHoursYearData[]
): NonWorkingHoursConversionResult => {
  const invalidValueErrors: string[] = [];
  const workingHoursErrors: string[] = [];
  const nonWorkingHourErrors: string[] = [];

  const rowsPerYear = 6 + NonWorkingItemsDisplayOrder.length + 1;

  // データが足りない分空配列を追加
  if (existingData.length * rowsPerYear > values.length) {
    const minus = existingData.length * rowsPerYear - values.length;
    for (let i = 0; i < minus; i++) {
      values.push([]);
    }
  }

  /**
   * 時間形式のバリデーション
   */
  const validateTimeFormat = (value: any, fieldName?: string, location?: string): string => {
    if (value === null || value === undefined || value === "") {
      return DEFAULT_TIME;
    }
    const strValue = String(value).trim();

    // 整数（0-23）の場合、時間に変換
    const intRegex = /^(\d+)$/;
    const intMatch = strValue.match(intRegex);
    if (intMatch) {
      const hours = parseInt(intMatch[1], 10);
      if (hours >= 0 && hours <= 23) {
        return `${hours.toString().padStart(2, '0')}:00`;
      }
      if (fieldName && location) {
        invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
          location, fieldName, value: strValue, defaultValue: DEFAULT_TIME
        }));
      }
      return DEFAULT_TIME;
    }

    // HH:MM形式をチェック
    const timeRegex = /^(\d{1,2}):(\d{2})$/;
    const match = strValue.match(timeRegex);
    if (!match) {
      if (fieldName && location) {
        invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
          location, fieldName, value: strValue, defaultValue: DEFAULT_TIME
        }));
      }
      return DEFAULT_TIME;
    }
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      if (fieldName && location) {
        invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
          location, fieldName, value: strValue, defaultValue: DEFAULT_TIME
        }));
      }
      return DEFAULT_TIME;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  /**
   * 勤務体制のバリデーション
   */
  const validateShiftSystem = (value: any, location?: string): string => {
    const validValues = ["0", "1", "2", "3"];
    const strValue = String(value).trim();
    if (validValues.includes(strValue)) {
      return strValue;
    }
    if (strValue !== "" && location) {
      invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.shiftSystemInvalid`, {
        location, value: strValue, defaultValue: DEFAULT_SHIFT_SYSTEM
      }));
    }
    return DEFAULT_SHIFT_SYSTEM;
  };

  // 各項目取得
  // ※「残業有無」行（index % rowsPerYear === 4）は表示専用（残業時間欄から算出）のため読み込み対象外
  const workTimeArray = values.filter((_, index) => index % rowsPerYear === 1);
  const overTimeArray = values.filter((_, index) => index % rowsPerYear === 2);
  const shiftSystemArray = values.filter((_, index) => index % rowsPerYear === 3);

  // 非稼働項目取得（画面表示順で1項目1行）
  const nonWorkingItemsArrays: string[][][] = [];
  for (let yearIndex = 0; yearIndex < existingData.length; yearIndex++) {
    const yearItems: string[][] = [];
    for (let itemIndex = 0; itemIndex < NonWorkingItemsDisplayOrder.length; itemIndex++) {
      const rowIndex = yearIndex * rowsPerYear + 6 + itemIndex;
      yearItems.push(values[rowIndex] || Array(12).fill(""));
    }
    nonWorkingItemsArrays.push(yearItems);
  }

  // データ変換
  const convertedData = existingData.map((yearData, yearIndex) => {
    const shiftedWorkTime = shiftToJanuaryStart(workTimeArray[yearIndex] || []);
    const shiftedOverTime = shiftToJanuaryStart(overTimeArray[yearIndex] || []);
    const shiftedShiftSystem = shiftToJanuaryStart(shiftSystemArray[yearIndex] || []);
    const shiftedNonWorkingItems = nonWorkingItemsArrays[yearIndex]?.map(items =>
      shiftToJanuaryStart(items || [])
    ) || [];

    return {
      ...yearData,
      nonWorkingHoursByMonth: yearData.nonWorkingHoursByMonth.map((monthData, monthIndex) => {
        const location = `${yearData.fiscalYear} ${getMonthNames()[monthIndex]}`;

        // 非稼働項目を更新（項目IDとシート行の対応は画面表示順（NonWorkingItemsDisplayOrder）で解決する。
        // 配列の並び順に依存しないため、項目の追加・並び替えがあっても既存データの対応がずれない）
        // 既存データの配列を書き換える方式では、項目が追加される前に保存されたデータに
        // 存在しない項目（休憩時間・計画停止など）の取り込み値が失われるため、
        // 正規の項目一覧から再構築する
        const itemNames = NON_WORKING_ITEM_NAMES();
        const updatedNonWorkingItems = NonWorkingItemsDisplayOrder.map((code, rowIndex) => ({
          nonWorkingId: code,
          nonWorkingHour: validateTimeFormat(
            shiftedNonWorkingItems[rowIndex]?.[monthIndex],
            itemNames[rowIndex] || i18n.t(`${T_PREFIX}.rowLabel.nonWorkingItemFallback`, { index: rowIndex + 1 }),
            location
          ),
        }));

        const validatedWorkTime = validateTimeFormat(shiftedWorkTime[monthIndex], i18n.t(`${T_PREFIX}.rowLabel.standardWorkTime`), location);
        const validatedOverTime = validateTimeFormat(shiftedOverTime[monthIndex], i18n.t(`${T_PREFIX}.rowLabel.overTime`), location);

        // 非稼働時間の合計（分）を計算
        const totalNonWorkingMinutes = updatedNonWorkingItems?.reduce((sum, item) => {
          return sum + hourMinutesStringToMinutes(item.nonWorkingHour);
        }, 0) || 0;

        // 稼働時間 = 24h - 非稼働時間の合計
        const operatingMinutes = 24 * 60 - totalNonWorkingMinutes;
        const operatingHour = minutesToHourMinutesString(operatingMinutes);

        // 非稼働時間
        const nonWorkingHour = minutesToHourMinutesString(totalNonWorkingMinutes);

        // 労働時間 = 標準労働時間 + 残業時間
        const workTimeMinutes = hourMinutesStringToMinutes(validatedWorkTime);
        const overTimeMinutes = hourMinutesStringToMinutes(validatedOverTime);
        const workingHours = minutesToHourMinutesString(workTimeMinutes + overTimeMinutes);

        // 稼働率
        const availabilityRate = parseFloat((operatingMinutes / (24 * 60)).toFixed(3));

        // 非稼働率
        const unavailabilityRate = parseFloat((totalNonWorkingMinutes / (24 * 60)).toFixed(3));

        return {
          ...monthData,
          workTime: validatedWorkTime,
          overTime: validatedOverTime,
          shiftSystem: validateShiftSystem(shiftedShiftSystem[monthIndex], location),
          nonWorkingItems: updatedNonWorkingItems,
          operatingHour,
          nonWorkingHour,
          workingHours,
          availabilityRate,
          unavailabilityRate,
        };
      })
    };
  });

  // バリデーションチェック
  const MAX_MINUTES = 24 * 60;
  convertedData.forEach(yearData => {
    yearData.nonWorkingHoursByMonth.forEach((monthData, monthIndex) => {
      const fiscalYear = yearData.fiscalYear;
      const monthName = getMonthNames()[monthIndex];

      // 労働時間のチェック
      const workTimeMinutes = hourMinutesStringToMinutes(monthData.workTime);
      const overTimeMinutes = hourMinutesStringToMinutes(monthData.overTime);
      const shiftSystem = Number(monthData.shiftSystem);
      const totalWorkingMinutes = (workTimeMinutes + overTimeMinutes) * shiftSystem;
      if (totalWorkingMinutes < 0 || totalWorkingMinutes > MAX_MINUTES) {
        workingHoursErrors.push(i18n.t(`${T_PREFIX}.message.workingHoursExceedMax`, {
          location: `${fiscalYear} ${monthName}`, defaultValue: DEFAULT_TIME
        }));
        monthData.workTime = DEFAULT_TIME;
        monthData.overTime = DEFAULT_TIME;
        monthData.workingHours = DEFAULT_TIME;
      }

      // 非稼働時間のチェック
      const totalNonWorkingMinutes = monthData.nonWorkingItems?.reduce((sum, item) => {
        return sum + hourMinutesStringToMinutes(item.nonWorkingHour);
      }, 0) || 0;
      const operatingMinutes = MAX_MINUTES - totalNonWorkingMinutes;
      if (operatingMinutes < 0) {
        nonWorkingHourErrors.push(i18n.t(`${T_PREFIX}.message.nonWorkingHoursExceedMax`, {
          location: `${fiscalYear} ${monthName}`, defaultValue: DEFAULT_TIME
        }));
        monthData.nonWorkingItems?.forEach(item => {
          item.nonWorkingHour = DEFAULT_TIME;
        });
        monthData.nonWorkingHour = DEFAULT_TIME;
        monthData.operatingHour = minutesToHourMinutesString(MAX_MINUTES);
        monthData.availabilityRate = 1;
        monthData.unavailabilityRate = 0;
      }
    });
  });

  return {
    data: convertedData,
    errors: {
      workingHoursErrors,
      nonWorkingHourErrors,
      invalidValueErrors,
    },
  };
};

/**
 * 非稼働時間SpreadSheet同期用カスタムフック
 */
export const useNonWorkingHoursSpreadSheet = (
  googleAPI: GoogleAPI,
  toast: (options: { title: string; description: React.ReactNode; status: 'error' | 'warning' | 'success' | 'info'; duration: number; isClosable: boolean }) => void,
  existingData: NonWorkingHoursYearData[]
) => {
  const config = useMemo<SpreadSheetSyncConfig<NonWorkingHoursYearData[]>>(() => ({
    folderPath: "CEAS/SpreadSheet/NonWorkingHours",
    fileName: i18n.t(`${T_PREFIX}.fileName`),
    sharedDriveName: null,
    toSpreadSheetData: nonWorkingHoursToSpreadSheetData,
    fromSpreadSheetData: (values) => spreadSheetDataToNonWorkingHours(values, existingData).data,
    getRange: (sheetName) => `${sheetName}!B2:M`,
  }), [existingData]);

  const syncResult = useSpreadSheetSync(config, googleAPI, toast);

  // データ連携時にエラーも取得できるようにラップ
  const handleSyncDataWithErrors = useCallback(async (): Promise<NonWorkingHoursConversionResult | null> => {
    const rawResult = await syncResult.handleSyncData();
    if (!rawResult) return null;
    return {
      data: rawResult,
      errors: {
        workingHoursErrors: [],
        nonWorkingHourErrors: [],
        invalidValueErrors: [],
      }
    };
  }, [syncResult]);

  return {
    ...syncResult,
    handleSyncDataWithErrors,
  };
};

export default useNonWorkingHoursSpreadSheet;
